import { z } from 'zod';

import { PartyFamilyRoleSchema, PartyGenderSchema } from '../customer/party-pf-profile.schemas.js';

const CivilDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const asUtc = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(asUtc.getTime()) && asUtc.toISOString().slice(0, 10) === value;
  }, 'invalid civil date');

const LocalTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const PLACE_CAMPAIGN_RECURRENCES = ['none', 'daily', 'weekly', 'monthly', 'yearly'] as const;
export const PlaceCampaignRecurrenceSchema = z.enum(PLACE_CAMPAIGN_RECURRENCES);
export type PlaceCampaignRecurrence = z.infer<typeof PlaceCampaignRecurrenceSchema>;

export const PLACE_CAMPAIGN_KINDS = ['custom', 'birthday', 'useful_life_due'] as const;
export const PlaceCampaignKindSchema = z.enum(PLACE_CAMPAIGN_KINDS);
export type PlaceCampaignKind = z.infer<typeof PlaceCampaignKindSchema>;

const PlaceNotificationCampaignContentSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    title: z.string().trim().min(1).max(120),
    body: z.string().trim().min(1).max(500),
    civilDate: CivilDateSchema.nullable(),
    localTime: LocalTimeSchema.nullable(),
    recurrence: PlaceCampaignRecurrenceSchema,
    isActive: z.boolean(),
    deliverInbox: z.boolean(),
    deliverPush: z.boolean(),
    deliverChat: z.boolean(),
    deliverWhatsapp: z.boolean().default(false),
    mediaUrl: z.string().min(1).nullable().optional(),
    mediaCaption: z.string().trim().max(500).nullable().optional(),
    kind: PlaceCampaignKindSchema.optional().default('custom'),
    consumerIds: z.array(z.uuid()).optional().default([]),
  })
  .refine(
    (value) => value.kind !== 'custom' || value.civilDate === null || value.localTime !== null,
    {
      message: 'civilDate requires localTime',
      path: ['localTime'],
    },
  )
  .refine(
    (value) =>
      value.kind !== 'custom' ||
      value.recurrence !== 'none' ||
      (value.civilDate === null) === (value.localTime === null),
    {
      message: 'civilDate and localTime must be provided together',
      path: ['civilDate'],
    },
  )
  .refine(
    (value) => value.kind !== 'custom' || value.recurrence === 'none' || value.localTime !== null,
    {
      message: 'recurrence requires localTime',
      path: ['localTime'],
    },
  )
  .refine(
    (value) =>
      value.deliverInbox || value.deliverPush || value.deliverChat || value.deliverWhatsapp,
    {
      message: 'at least one delivery channel is required',
      path: ['deliverInbox'],
    },
  )
  .refine((value) => value.kind !== 'custom' || value.consumerIds.length >= 1, {
    message: 'consumerIds must contain at least one recipient for custom campaigns',
    path: ['consumerIds'],
  });

export const PlaceNotificationCampaignBodySchema = PlaceNotificationCampaignContentSchema;
export type PlaceNotificationCampaignBody = z.infer<typeof PlaceNotificationCampaignBodySchema>;

export const PLACE_CAMPAIGN_DISPATCH_STATES = [
  'ready',
  'scheduled',
  'skipped',
  'dispatched',
] as const;
export const PlaceCampaignDispatchStateSchema = z.enum(PLACE_CAMPAIGN_DISPATCH_STATES);
export type PlaceCampaignDispatchState = z.infer<typeof PlaceCampaignDispatchStateSchema>;

export const PlaceNotificationCampaignResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  kind: PlaceCampaignKindSchema,
  name: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
  civilDate: CivilDateSchema.nullable(),
  localTime: LocalTimeSchema.nullable(),
  recurrence: PlaceCampaignRecurrenceSchema,
  deliverInbox: z.boolean(),
  deliverPush: z.boolean(),
  deliverChat: z.boolean(),
  deliverWhatsapp: z.boolean(),
  mediaUrl: z.string().nullable(),
  mediaStorageKey: z.string().min(1).nullable().optional(),
  mediaCaption: z.string().nullable(),
  recipientsAttempted: z.number().int().nonnegative().optional(),
  isActive: z.boolean(),
  dispatchState: PlaceCampaignDispatchStateSchema,
  consumerIds: z.array(z.uuid()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PlaceNotificationCampaignResponse = z.infer<
  typeof PlaceNotificationCampaignResponseSchema
>;

export const ListPlaceNotificationCampaignsResponseSchema = z.object({
  items: z.array(PlaceNotificationCampaignResponseSchema),
});
export type ListPlaceNotificationCampaignsResponse = z.infer<
  typeof ListPlaceNotificationCampaignsResponseSchema
>;

export const PreviewPlaceNotificationCampaignAudienceResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  items: z.array(
    z.object({
      consumerId: z.uuid(),
      displayName: z.string().min(1),
    }),
  ),
  referenceDate: CivilDateSchema.optional(),
});
export type PreviewPlaceNotificationCampaignAudienceResponse = z.infer<
  typeof PreviewPlaceNotificationCampaignAudienceResponseSchema
>;

/** Optional PF recorte on audience preview (BR-NOT-26). Omitted filters keep BR-NOT-11. */
export const PreviewPlaceNotificationCampaignAudienceQuerySchema = z.object({
  kind: PlaceCampaignKindSchema.optional().default('custom'),
  referenceDate: CivilDateSchema.optional(),
  gender: PartyGenderSchema.optional(),
  familyRole: PartyFamilyRoleSchema.optional(),
  occupation: z.string().trim().min(1).max(120).optional(),
  birthMonth: z.coerce.number().int().min(1).max(12).optional(),
  requireMarketingConsent: z.enum(['true', 'false']).optional(),
});
export type PreviewPlaceNotificationCampaignAudienceQuery = z.infer<
  typeof PreviewPlaceNotificationCampaignAudienceQuerySchema
>;

export const GeneratePlaceCampaignContentBodySchema = z.object({
  briefing: z.string().trim().min(1),
  kind: PlaceCampaignKindSchema.optional().default('custom'),
});
export type GeneratePlaceCampaignContentBody = z.infer<
  typeof GeneratePlaceCampaignContentBodySchema
>;

export const GeneratePlaceCampaignContentResponseSchema = z.object({
  title: z.string().min(1).max(120),
  body: z.string().min(1).max(500),
  mediaUrl: z.string().min(1).nullable(),
});
export type GeneratePlaceCampaignContentResponse = z.infer<
  typeof GeneratePlaceCampaignContentResponseSchema
>;

export const ArchivePlaceNotificationCampaignResponseSchema = z.object({
  id: z.uuid(),
  archivedAt: z.iso.datetime(),
});
export type ArchivePlaceNotificationCampaignResponse = z.infer<
  typeof ArchivePlaceNotificationCampaignResponseSchema
>;

export const PlaceNotificationCampaignPlaceParamsSchema = z.object({
  placeId: z.uuid(),
});

export const PlaceNotificationCampaignParamsSchema = z.object({
  placeId: z.uuid(),
  campaignId: z.uuid(),
});
